import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import {
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { finalize, Observable, Subject, takeUntil } from 'rxjs';
import Swal from 'sweetalert2';

import { AuthService } from '@app/core/auth';
import { Factory, FactoryService } from '@app/core/factories';
import { SettingsService } from '@app/core/settings/services/settings.service';
import { StaffService } from '@app/core/staff';
import { ContactApiService } from '@app/core/staff/services/contact-api.service';
import { EResourceViewMode } from '@app/shared/resource-view-mode.const';
import { IContactDetails } from 'v2/modules/shared/interfaces/contact-details.interface';
import { trackByLabel } from 'v2/modules/shared/util/track-by.util';
import { SupplierContactFormComponent } from 'v2/modules/supplier/components';

@Component({
  selector: 'cnc-form-modal',
  templateUrl: './form-modal.component.html',
  styleUrls: ['./form-modal.component.scss'],
})
export class AuthFormModalComponent implements OnInit, OnDestroy {
  hide :boolean = true;
  loading :boolean = false;
  submitted :boolean = false;
  pageTitle :string = this.translate.instant('TITLES.NEW_SUPPLIER');
  formData :UntypedFormGroup;
  factoryAdmins :any = [];
  loadingContact :boolean = false;
  contacts :IContactDetails[] = [];
  viewMode :EResourceViewMode = EResourceViewMode.CREATE;

  trackByLabel = trackByLabel;

  @Input() factory :Factory;

  private onDestroy$ = new Subject<void>();
  paymentTerms$ :Observable<any>;

  constructor(
    public activeModal :NgbActiveModal,
    private dialog :MatDialog,
    private fb :UntypedFormBuilder,
    private factoryService :FactoryService,
    public staffService :StaffService,
    private toastr :ToastrService,
    private translate :TranslateService,
    public authService :AuthService,
    private contactApiService :ContactApiService,
    private settingService :SettingsService,
  ) {}

  ngOnInit() {
    this.formData = this.fb.group({
      factory_name: ['', Validators.required],
      factory_admin_id: [''],
      factory_description: [''],
      street_address: ['', Validators.required],
      city: ['', Validators.required],
      state: ['', Validators.required],
      zip: ['', Validators.required],
      country: ['', Validators.required],
      bank_name: [''],
      routing_number: [''],
      account_number: [''],
      swift_code: [''],
      contacts: this.fb.array([]),
      default_payment_term: [this.factory?.default_payment_term || ''],
    });

    this.viewMode = this.factory
      ? EResourceViewMode.UPDATE
      : EResourceViewMode.CREATE;

    if (this.viewMode === EResourceViewMode.UPDATE) {
      this.pageTitle = this.translate.instant('TITLES.EDIT_SUPPLIER');
      this.formData.patchValue(this.factory);
      this.getContacts(this.factory.id);
    } else {
      // Supplier Admins creating new suppliers are assigned as admin; they
      // can't edit the supplier admin field.
      if (this.authService.loginUser.role == 8) {
        this.formData.patchValue({
          factory_admin_id: this.authService.loginUser.id,
        });
      }
    }

    this.staffService.staff$.subscribe((staff :any) => {
      this.factoryAdmins = staff.filter((row) => row.role == 8);
      if (this.authService.loginUser.role >= 8) {
        this.factoryAdmins.splice(0, 0, this.authService.loginUser);
      }
    });

    this.getContactList();
    this.getPaymentTerms();
    this.initObservables();
  }

  ngOnDestroy() :void {
    this.onDestroy$.next(null);
    this.onDestroy$.complete();
  }

  onCountryChange(country :string) :void {
    this.formData.controls.country.setValue(country);
  }

  onOpenContactDialog() :void {
    this.dialog
      .open(SupplierContactFormComponent, {
        width: '600px',
        data: {},
      })
      .afterClosed()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((result) => {
        if (result) {
          this.getContactList(null);
          this.contactsControl.push(this.fb.control(result));
        }
      });
  }

  onChangeContactEvent(event :any) :void {
    this.getContactList(event);
  }

  onSelectContactEvent(contact :IContactDetails) :void {
    const contactExists = this.contactsControl.value.find(
      (row :any) => row.id == contact.id,
    );
    if (!contactExists) {
      this.contactsControl.push(this.fb.control(contact));
    }
  }

  onRemoveContact(contact :IContactDetails) :void {
    const index = this.contactsControl.controls.findIndex(
      (control) => control.value.id === contact.id,
    );

    this.contactsControl.removeAt(index);
  }

  onSubmit() {
    this.submitted = true;
    if (this.formData.invalid && this.contactsControl.length === 0) {
      Swal.fire({
        title: this.translate.instant('MESSAGES.FORM_ERROR.TITLE'),
        text: this.translate.instant('MESSAGES.FORM_ERROR.TEXT'),
        type: 'error',
        confirmButtonText: this.translate.instant('BUTTONS.OKAY'),
      });

      return;
    }
    this.loading = true;

    const contactIds = this.contactsControl.value.map(
      (row :IContactDetails) => row.id,
    );
    const data = {
      ...this.formData.value,
      contactIds,
    };

    if (this.factory && this.factory.id) {
      this.factoryService.updateRecord(data, this.factory.id).subscribe(
        (resp :any) => {
          this.toastr.success(
            this.translate.instant('MESSAGES.UPDATE_SUCCESS.TEXT'),
            this.translate.instant('MESSAGES.UPDATE_SUCCESS.TITLE'),
          );
          this.activeModal.close(resp.record);
        },
        (error) => {
          this.loading = false;
          const errors = error.error;
          for (const [error_key, error] of Object.entries(errors)) {
            for (const [message_key, message] of Object.entries(error)) {
              this.toastr.error(message);
            }
          }
        },
      );
    } else {
      this.factoryService.createRecord(data).subscribe(
        (resp :any) => {
          this.toastr.success(resp.message, 'Save Successful!');
          this.activeModal.close(resp.record);
        },
        (error) => {
          this.loading = false;
          const errors = error.error;
          for (const [error_key, error] of Object.entries(errors)) {
            for (const [message_key, message] of Object.entries(error)) {
              this.toastr.error(message);
            }
          }
        },
      );
    }
  }

  private getContactList(keyword ?:string) :void {
    this.loadingContact = true;

    this.contactApiService
      .getList({ ...(keyword && { keyword }) })
      .pipe(
        takeUntil(this.onDestroy$),
        finalize(() => (this.loadingContact = false)),
      )
      .subscribe({
        next: (contacts :IContactDetails[]) => {
          this.contacts = contacts;
        },
      });
  }

  private getContacts(factoryId :number) :void {
    this.contactApiService
      .getFactoryContacts(factoryId)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((resp :any) => {
        this.setContacts(resp);
      });
  }

  private setContacts(contacts :any) :void {
    this.contactsControl.clear();

    if (contacts) {
      contacts.forEach((row) => {
        this.contactsControl.push(this.fb.control(row?.contactDetails));
      });
    }
  }

  private getPaymentTerms() :void {
    this.settingService.paymentTerms$.pipe(takeUntil(this.onDestroy$)).subscribe({
      next: (paymentTerms) => {
        if (paymentTerms?.length && !this.factory) {
          this.formData.patchValue({
            default_payment_term: paymentTerms[0].label,
          });
        }
      },
    });
  }

  private initObservables() :void {
    this.paymentTerms$ = this.settingService.paymentTerms$;
  }

  get contactsControl() :UntypedFormArray {
    return this.formData.get('contacts') as UntypedFormArray;
  }
}
